import { useState } from "react";
import { useMatch, useNavigate } from "react-router-dom";
import {
  Copy,
  MoreHorizontal,
  Download,
  PencilLine,
  Loader2,
  CloudOff,
  Upload,
  Users,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { getFetcher, putFetcher } from "api/swr";
import { SpineAvatar } from "../../types";
import {
  openAvatarDrawDataInEditor,
  resolveMainTemplateIdForAvatar,
} from "AdminPanel/Spine/lib/outfitFromDrawData";
import { mutate } from "swr";
import useSWRMutation from "swr/mutation";

/** 列表项常无 drawData，按需拉详情（含 drawData） */
async function resolveAvatarWithDrawData(
  avatar: SpineAvatar,
): Promise<SpineAvatar | null> {
  if (avatar.drawData?.trim()) {
    return avatar;
  }
  const detail = await getFetcher(
    `/spine-avatars/${encodeURIComponent(avatar.id)}`,
  );
  if (!detail || typeof detail !== "object") {
    return null;
  }
  return { ...avatar, ...detail } as SpineAvatar;
}

interface AvatarActionsProps {
  avatar: SpineAvatar;
  /** 列表筛选 tid 或编辑器当前模版，用于推断主模版 */
  tid?: string;
}

export function AvatarActions({ avatar, tid }: AvatarActionsProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const editorMatch = useMatch("/admin/spine/editor/:tid");
  const [openingEditor, setOpeningEditor] = useState(false);
  const { trigger: updateState, isMutating: updatingState } = useSWRMutation(
    `/spine-avatars/${encodeURIComponent(avatar.id)}/state`,
    putFetcher,
  );

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(avatar.id);
      toast({
        title: "已复制",
        description: `Avatar ID: ${avatar.id}`,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "复制失败",
        description: "无法复制到剪贴板",
      });
    }
  };

  const [exportingDrawData, setExportingDrawData] = useState(false);

  const handleToggleOffline = async () => {
    if (!avatar.id || updatingState) {
      return;
    }

    const nextState = avatar.state === 401 ? 0 : 401;
    mutate(
      (key) => Array.isArray(key) && key[0] === "/spine-avatars",
      (currentData: SpineAvatar[] | undefined) => {
        if (!Array.isArray(currentData)) {
          return currentData;
        }
        return currentData.map((item) =>
          item.id === avatar.id ? { ...item, state: nextState } : item,
        );
      },
      { revalidate: false },
    );

    try {
      await updateState({ state: nextState });
      await mutate((key) => Array.isArray(key) && key[0] === "/spine-avatars");
      toast({
        title: "更新成功",
        description: nextState === 401 ? "Avatar 已上线" : "Avatar 已下线",
      });
    } catch (err) {
      await mutate((key) => Array.isArray(key) && key[0] === "/spine-avatars");
      toast({
        variant: "destructive",
        title: "更新失败",
        description: err instanceof Error ? err.message : "Avatar 状态更新失败",
      });
    }
  };

  const handleExportDrawData = async () => {
    if (!avatar.id) {
      toast({
        variant: "destructive",
        title: "导出失败",
        description: "缺少 Avatar ID",
      });
      return;
    }

    setExportingDrawData(true);
    try {
      const resolved = await resolveAvatarWithDrawData(avatar);
      if (!resolved?.drawData?.trim()) {
        toast({
          variant: "destructive",
          title: "导出失败",
          description: "drawData 为空",
        });
        return;
      }

      let parsedData: unknown;
      try {
        parsedData = JSON.parse(resolved.drawData);
      } catch {
        toast({
          variant: "destructive",
          title: "导出失败",
          description: "drawData 格式无效",
        });
        return;
      }

      const jsonString = JSON.stringify(parsedData, null, 2);
      const blob = new Blob([jsonString], { type: "application/json" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `avatar-${avatar.id}-drawData.json`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "导出成功",
        description: "drawData 已导出为 JSON 文件",
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "导出失败",
        description: err instanceof Error ? err.message : "导出失败",
      });
    } finally {
      setExportingDrawData(false);
    }
  };

  const handleOpenInEditor = async () => {
    if (!avatar.id) {
      toast({
        variant: "destructive",
        title: "无法打开",
        description: "缺少 Avatar ID",
      });
      return;
    }

    setOpeningEditor(true);
    try {
      const resolved = await resolveAvatarWithDrawData(avatar);
      if (!resolved) {
        toast({
          variant: "destructive",
          title: "加载失败",
          description: "未找到该 Avatar 或接口无数据",
        });
        return;
      }
      if (!resolved.drawData?.trim()) {
        toast({
          variant: "destructive",
          title: "无法打开",
          description: "详情中 drawData 为空",
        });
        return;
      }

      const mainTid = resolveMainTemplateIdForAvatar(resolved, tid);
      if (!mainTid) {
        toast({
          variant: "destructive",
          title: "无法打开",
          description:
            "无法确定模版 tid（缺少 tid 且 drawData 中无 templateId）",
        });
        return;
      }

      await openAvatarDrawDataInEditor({
        avatar: resolved,
        tid,
        currentEditorTid: editorMatch?.params?.tid ?? null,
        navigate,
        onError: (message) => {
          toast({
            variant: "destructive",
            title: "打开编辑器失败",
            description: message,
          });
        },
        onDone: () => {
          const same = editorMatch?.params?.tid === mainTid;
          if (same) {
            toast({
              title: "已应用搭配",
              description: "预览已按 Avatar drawData 更新",
            });
          }
        },
      });
    } finally {
      setOpeningEditor(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
        >
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[200px]">
        <DropdownMenuItem onClick={handleCopyId}>
          <Copy className="mr-2 h-4 w-4" />
          Copy ID
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!avatar.id || openingEditor || exportingDrawData}
          onClick={() => void handleOpenInEditor()}
        >
          {openingEditor ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <PencilLine className="mr-2 h-4 w-4" />
          )}
          Editor
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!avatar.id || exportingDrawData || openingEditor}
          onClick={() => void handleExportDrawData()}
        >
          {exportingDrawData ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Download className="mr-2 h-4 w-4" />
          )}
          Export DrawData
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!avatar.id || openingEditor || exportingDrawData}
          onClick={() =>
            navigate(
              `/admin/spine-avatars?aid=${encodeURIComponent(avatar.id)}`,
            )
          }
        >
          <Users className="mr-2 h-4 w-4" />
          Sub Avatars
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={
            !avatar.id || updatingState || openingEditor || exportingDrawData
          }
          onClick={() => void handleToggleOffline()}
          className="text-destructive focus:text-destructive"
        >
          {updatingState ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : avatar.state === 401 ? (
            <CloudOff className="mr-2 h-4 w-4" />
          ) : (
            <Upload className="mr-2 h-4 w-4" />
          )}
          {avatar.state === 401 ? "Set to offline" : "Set to online"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
